import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import type {
  ContextSize,
  PendingInputRequest,
  SubmitInputResponseArgs,
  SystemNotification,
  Turn,
  WorkspaceTab,
} from '../../hooks/useWorkspace';
import WorkspaceTurnList from './turns/WorkspaceTurnList';
import AgentInputRequestBanner from './turns/AgentInputRequestBanner';

/* ---------- styled ---------- */

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-right: 1px solid var(--border);
  background: var(--bg-surface);
`;

const ContextBar = styled.div`
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const ContextTrack = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: var(--bg-elevated);
  overflow: hidden;
`;

const ContextFill = styled.div<{ $pct: number }>`
  height: 100%;
  border-radius: 3px;
  width: ${({ $pct }) => Math.min($pct, 100)}%;
  background: ${({ $pct }) =>
    $pct > 75
      ? 'var(--danger, #ef4444)'
      : $pct > 50
        ? 'var(--warning, #eab308)'
        : 'var(--accent)'};
  transition: width 0.4s ease, background 0.4s ease;
`;

const InputArea = styled.div`
  padding: 0.75rem 1rem 1rem;
  border-top: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InputWrap = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  background: var(--input-bg);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 0.375rem;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: var(--accent);
  }
`;

const Textarea = styled.textarea`
  flex: 1;
  padding: 0.375rem 0.625rem;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--text-primary);
  outline: none;
  resize: none;
  max-height: 120px;
  line-height: 1.5;

  &::placeholder {
    color: var(--text-muted);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SendBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: var(--accent);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover {
    background: var(--accent-hover);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StopBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: var(--danger, #ef4444);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover {
    background: var(--danger-hover, #dc2626);
  }
`;

const ModelSelect = styled.select`
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--input-bg);
  font-family: inherit;
  font-size: 0.75rem;
  color: var(--text-secondary);
  outline: none;
  cursor: pointer;
`;

const InputFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.375rem;
  font-size: 0.6875rem;
  color: var(--text-muted);
`;

/* ---------- component ---------- */

interface WorkspaceChatPanelProps {
  turns: Turn[];
  pendingInputRequest: PendingInputRequest | null;
  systemNotifications: SystemNotification[];
  contextSize: ContextSize | null;
  isProcessing: boolean;
  /**
   * True when at least one turn is still actively running. The stop button
   * is gated on this so it cannot stick "on" after the final reply has been
   * delivered but ``isProcessing`` has not yet been cleared by a stale WS
   * event (Issue #3).
   */
  hasActiveTurn: boolean;
  isSending: boolean;
  activeTab: WorkspaceTab;
  onSend: (query: string) => void;
  onSubmitInputResponse: (args: SubmitInputResponseArgs) => void;
  onStop?: () => void;
  modeSelector?: React.ReactNode;
}

const MODELS = ['gpt-4o', 'gpt-4o-mini', 'claude-sonnet', 'claude-haiku'];

const WorkspaceChatPanel: React.FC<WorkspaceChatPanelProps> = ({
  turns,
  pendingInputRequest,
  systemNotifications,
  contextSize,
  isProcessing,
  hasActiveTurn,
  isSending,
  onSend,
  onSubmitInputResponse,
  onStop,
  modeSelector,
}) => {
  const [input, setInput] = useState('');
  const [model, setModel] = useState(MODELS[0]);
  const scrollToTurnIdRef = useRef<((turnId: string) => void) | null>(null);

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    onSend(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const pct = contextSize
    ? (contextSize.sessionTotalTokens / contextSize.modelContextLimit) * 100
    : 0;

  const hasPendingInput = !!pendingInputRequest;

  const handleJumpTo = (turnId: string) => {
    scrollToTurnIdRef.current?.(turnId);
  };

  return (
    <Panel>
      {contextSize && (
        <ContextBar>
          <span>Context</span>
          <ContextTrack>
            <ContextFill $pct={pct} />
          </ContextTrack>
          <span>
            {Math.round(pct)}% &middot; {contextSize.model}
          </span>
        </ContextBar>
      )}

      <WorkspaceTurnList
        turns={turns}
        pendingInputRequest={pendingInputRequest}
        systemNotifications={systemNotifications}
        onSubmitInputResponse={onSubmitInputResponse}
        scrollToTurnIdRef={scrollToTurnIdRef}
      />

      <InputArea>
        {hasPendingInput ? (
          <AgentInputRequestBanner
            request={pendingInputRequest}
            onJumpTo={handleJumpTo}
            onSubmit={onSubmitInputResponse}
            onStop={onStop}
          />
        ) : (
          <InputWrap>
            <Textarea
              rows={1}
              placeholder={
                hasPendingInput
                  ? 'Waiting for your answer above...'
                  : 'Ask anything...'
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={hasPendingInput}
              aria-disabled={hasPendingInput}
            />
            {isProcessing && hasActiveTurn && onStop ? (
              <StopBtn title="Stop" onClick={onStop}>
                &#x25A0;
              </StopBtn>
            ) : (
              <SendBtn
                title="Send"
                onClick={handleSend}
                disabled={isSending || !input.trim() || hasPendingInput}
              >
                &#x2191;
              </SendBtn>
            )}
          </InputWrap>
        )}
        <InputFooter>
          <ModelSelect value={model} onChange={(e) => setModel(e.target.value)}>
            {MODELS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </ModelSelect>
          {modeSelector}
        </InputFooter>
      </InputArea>
    </Panel>
  );
};

export default WorkspaceChatPanel;
